import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { MoneyInputError, formatWireForDisplay, parseMajorInput } from '../money.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import {
  invoiceMatchStatusWord,
  matchFindingWord,
  matchVerdictWord,
  purchaseOrderStatusWord,
} from '../status-labels.js'
import type { PurchaseOrderData } from './Purchasing.js'

/**
 * The supplier's invoice, and `INV-006`'s three-way match. §6.10 `INV-006`, Appendix W.5,
 * §15.6 step 4.
 *
 * `Purchasing.tsx` stops at the goods receipt and says so in terms: entering a supplier invoice and
 * running the match "are an accountant's job at a desk rather than a counter's at a delivery". This
 * is that desk. Four endpoints had no caller anywhere in the product, so an invoice could arrive on
 * paper and never be entered, and §15.6 step 4 ended at the dock.
 *
 * **The receipt line id is why this screen can exist at all.** `EnterInvoice.lines[]` cites a
 * `goodsReceiptLineId`, and the only place one used to appear was the `POST /receipts` response,
 * which takes `stock_receive`: the dispenser at the dock holds that row and the accountant does not.
 * The ids are now on `GET /purchase-orders/:id`, which is the read this screen drives, and the
 * service records why that was the fix rather than widening the receipt route.
 *
 * **Nothing on the invoice form is pre-filled, and that is the requirement rather than laziness.**
 * The whole of `INV-006` is comparing three independent statements: what was ordered, what arrived,
 * and what the supplier billed. A quantity box opened at `quantityReceived` and a cost box opened at
 * the agreed `unitCost` would make `invoice_quantity_over` and `price_variance` unreachable for
 * every accountant who tabbed through, and that is the one defect here nobody can find by reading
 * the result: a match that was never tested looks exactly like a match that passed. So the ordered
 * and received figures and the agreed cost are printed **beside** each field, to be read against the
 * paper, and every box starts empty.
 *
 * **Without the cost grant this screen offers no write.** `orderForCaller` removes `total` and every
 * `unitCost` from a reader without Appendix B's cost-price row at this branch (`ROLE-001a`), and the
 * order's currency travels only inside `total`. An amount cannot be turned into minor units without
 * the currency's decimal places (`MR-01`, and `money.ts`'s `MINOR_DECIMALS`), so a screen that
 * carried on would guess a currency and send a figure at the wrong scale. Entering a supplier
 * invoice is a cost-price job by nature, so the form is replaced by a sentence. Absent means
 * withheld here with no second reading: `view()` builds the total unconditionally and every order
 * has one, so unlike `PRM-004`'s margin floor there is no "there is none" to keep apart.
 *
 * **Five certain refusals are pre-checked, because a refused write stops the whole queue**
 * (`OFF-005`, and `later_todo.md` 10: a refused write still holds the queue and nothing prunes it).
 *
 * - `matchThreeWay` throws `MATCH_NOT_ALLOWED` in any state but `partially_received` or `received`,
 *   so an order with no delivery against it is not offered an invoice form.
 * - `INVOICE_ALREADY_JUDGED` (409) refuses a second match on anything but an `unmatched` invoice.
 * - `INVOICE_LINE_PRICES_DIFFER` (422) refuses two receipt lines against the same **order** line
 *   billed at two prices, because the domain compares one price per order line and sums the
 *   quantities. The screen knows which order line each receipt line belongs to, so it can say this
 *   before sending it.
 * - `NOT_A_FOREIGN_AMOUNT` refuses a rate on an invoice already in the base currency and
 *   `FOREIGN_RATE_REQUIRED` refuses one without where the currencies differ (`IMP-004`). They are
 *   the same three fields appearing and disappearing, so they are driven off the order's currency
 *   rather than left for the accountant to know about.
 * - `QUANTITY_INVALID`: `asPieces` wants one or more, so a receipt line the supplier did not bill is
 *   left out of the body rather than sent as a zero.
 *
 * **While the order is only `partially_received` the verdict will be `pending`, and the screen says
 * so before the write rather than after.** An invoice that bills ahead of the goods is the ordinary
 * case for goods in transit and not yet a disagreement, so it is stored `unmatched` and the order
 * does not move. Nothing but a human asking again ever re-judges it, which is what the match button
 * is for, and it is the only caller `POST .../match` will ever have.
 *
 * **The three tolerances are asked for every invoice, because nothing stores them.** `INV-006`
 * allows an over-receipt within tolerance and names no figure, and the same is true of the two match
 * tolerances; `later_todo.md` 18 records that the fix is a stored policy, that per supplier is the
 * more useful shape, and that it is blocked on a product call. Two consequences the service already
 * reports are repeated to the person typing: the match cannot be reproduced from the order later,
 * and the number they type is the one their own invoice is judged by.
 *
 * **Resolving a dispute is a different Appendix B row.** W.5 makes `disputed -> resolved` require a
 * manager, and `purchase_order_approve` is owner and manager and marked sensitive, while
 * `supplier_invoice_match` is owner, manager and accountant. The accountant who raised the query is
 * not the person who closes it. `transitionPurchaseOrder` checks the role against the grid as well,
 * so the button is about not offering a locked door rather than about locking it.
 */

export interface PurchaseOrderLineDetail {
  readonly id: string
  readonly sku: string
  readonly description: string
  readonly orderedQuantity: number
  readonly receivedQuantity: number
  /** Absent means Appendix B's cost row is withheld from this reader. */
  readonly unitCostMinor?: string
}

export interface GoodsReceiptLineDetail {
  /** The row a supplier invoice line cites. Reachable nowhere else for this reader. */
  readonly goodsReceiptLineId: string
  readonly purchaseOrderLineId: string
  readonly quantityReceived: number
  /** `INV-006`: more arrived on this line than was ordered, and it was accepted within tolerance. */
  readonly isOverReceipt: boolean
  readonly lot?: string
}

export interface GoodsReceiptDetail {
  readonly id: string
  readonly number: string
  readonly lines: readonly GoodsReceiptLineDetail[]
}

export interface SupplierInvoiceDetail {
  readonly id: string
  readonly supplierInvoiceNumber: string
  /** `unmatched`, `matched` or `queried`. Unnarrowed per `ARC-020`. */
  readonly matchStatus: string
  /** The blocking findings of the match that queried it, as the service wrote them. */
  readonly queryReason?: string
  /**
   * `IMP-004`: paid so far and still to pay, in the invoice's own currency, whole minor units.
   *
   * **Absent, like `unitCostMinor`, means Appendix B's cost row is withheld from this reader**, and
   * the payment form is then not offered at all: without what is left to pay the screen cannot
   * refuse an over-payment locally, and an over-payment is a 422 that halts the whole write queue.
   */
  readonly settledMinor?: string
  readonly outstandingMinor?: string
  /**
   * The `supplier_invoice` row's version, which a payment sends as `expectedVersion`.
   *
   * Not the order's. A payment locks and bumps the invoice, so the order's version would not move
   * and a second payment of one invoice would not be a conflict.
   */
  readonly version?: number
}

export interface PurchaseOrderDetailData {
  readonly id: string
  readonly number: string
  /** Appendix C.5's state. Unnarrowed per `ARC-020`. */
  readonly status: string
  readonly version: number
  /**
   * The order's own currency, read off the gated total.
   *
   * Absent means the cost grant is withheld, never that the order has no currency: every order has
   * one, and `view()` builds the total before `orderForCaller` decides who is told.
   */
  readonly currency?: string
  readonly totalMinor?: string
  readonly lines: readonly PurchaseOrderLineDetail[]
  readonly receipts: readonly GoodsReceiptDetail[]
  readonly invoices: readonly SupplierInvoiceDetail[]
}

export interface SupplierInvoiceProps {
  /** `GET /purchase-orders`, the same list the counter's purchasing screen reads. */
  readonly load: () => Promise<readonly PurchaseOrderData[]>
  /** `GET /purchase-orders/:id`, the only read carrying the receipt line ids. */
  readonly loadOrder: (purchaseOrderId: string) => Promise<PurchaseOrderDetailData>
  /**
   * The branch's own currency, which is `IMP-004`'s base. From the caller, never guessed: it is
   * what decides whether a rate is required or refused, and getting it wrong is a 422 either way.
   */
  readonly baseCurrency: string
  /** `purchase_order_approve`, W.5's manager. Owner and manager, and not the accountant. */
  readonly canResolveDispute: boolean
  /**
   * `supplier_invoice_settle`, the Appendix B row `decisions/0041` added. Owner, manager and
   * accountant, and marked sensitive because it moves money.
   *
   * **Absent, not disabled.** A disabled Record a payment button on an invoice somebody cannot pay
   * is a door with no handle: it says the shop can do this and that this person is the wrong one,
   * which is only useful where a colleague is standing next to them. The dispute button above is
   * the case where that is true, so it keeps its sentence; this one is simply not there.
   */
  readonly canSettle: boolean
}

/** The states `matchThreeWay` will judge. Anything else is `MATCH_NOT_ALLOWED`. */
const MATCHABLE: readonly string[] = ['partially_received', 'received']

/** `parseRate`: a decimal string with at most eight places, read as digits and never as a float. */
const RATE = /^\d{1,10}(\.\d{1,8})?$/

interface Finding {
  readonly kind: string
  readonly purchaseOrderLineId: string
  readonly blocking: boolean
  readonly withinTolerance: boolean
  readonly quantity?: number
  readonly amount?: { readonly minor: string; readonly currency: string }
}

interface MatchAnswer {
  readonly verdict: string
  readonly findings: readonly Finding[]
}

/**
 * One finding off a write's response, or undefined when it is not that shape.
 *
 * Narrowed field by field rather than cast, for `reads.ts`'s reason: a field the server does not
 * send arrives as `undefined` and typechecks all the way to the till. Never thrown: the invoice
 * itself was accepted, and an answer this build did not anticipate is a reason to say less rather
 * than to replace a confirmation with a blank screen.
 */
function findingOf(value: unknown): Finding | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const row = value as Record<string, unknown>
  if (typeof row['kind'] !== 'string' || typeof row['purchaseOrderLineId'] !== 'string') {
    return undefined
  }
  const amount = row['amount']
  const money =
    typeof amount === 'object' && amount !== null ? (amount as Record<string, unknown>) : undefined
  return {
    kind: row['kind'],
    purchaseOrderLineId: row['purchaseOrderLineId'],
    blocking: row['blocking'] === true,
    withinTolerance: row['withinTolerance'] === true,
    ...(typeof row['quantity'] === 'number' ? { quantity: row['quantity'] } : {}),
    ...(typeof money?.['minor'] === 'string' && typeof money['currency'] === 'string'
      ? { amount: { minor: money['minor'], currency: money['currency'] } }
      : {}),
  }
}

/** The verdict and the findings off an invoice or match response. */
function matchOf(response: unknown): MatchAnswer | undefined {
  if (typeof response !== 'object' || response === null) return undefined
  const match = (response as { match?: unknown }).match
  if (typeof match !== 'object' || match === null) return undefined
  const { verdict, findings } = match as { verdict?: unknown; findings?: unknown }
  if (typeof verdict !== 'string') return undefined
  return {
    verdict,
    findings: (Array.isArray(findings) ? findings : [])
      .map((row: unknown) => findingOf(row))
      .filter((row): row is Finding => row !== undefined),
  }
}

/** What the supplier billed on one receipt line, as typed. */
interface BilledDraft {
  readonly quantity: string
  readonly unitCost: string
}

const EMPTY_BILLED: BilledDraft = { quantity: '', unitCost: '' }

interface InvoiceLineBody {
  readonly goodsReceiptLineId: string
  readonly quantityInvoiced: number
  readonly unitCostMinor: string
}

export function SupplierInvoice({
  load,
  loadOrder,
  baseCurrency,
  canResolveDispute,
  canSettle,
}: SupplierInvoiceProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [rows, setRows] = useState<readonly PurchaseOrderData[] | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PurchaseOrderDetailData | null>(null)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoicedOn, setInvoicedOn] = useState('')
  const [dueOn, setDueOn] = useState('')
  const [billed, setBilled] = useState<Record<string, BilledDraft>>({})
  const [baseTotal, setBaseTotal] = useState('')
  const [rate, setRate] = useState('')
  const [rateOn, setRateOn] = useState('')
  const [overReceipt, setOverReceipt] = useState('')
  const [invoiceQuantity, setInvoiceQuantity] = useState('')
  const [priceTolerance, setPriceTolerance] = useState('')
  const [answer, setAnswer] = useState<MatchAnswer | null>(null)
  // Which invoice's payment form is open, and what has been typed into it. One form at a time: a
  // shop pays one invoice at a time, and a form per row would make the list unreadable.
  const [payingId, setPayingId] = useState<string | null>(null)
  const [paidAmount, setPaidAmount] = useState('')
  const [paidFrom, setPaidFrom] = useState<'cash' | 'bank'>('bank')
  const [paidOn, setPaidOn] = useState('')
  const [paidBase, setPaidBase] = useState('')
  const [paidRate, setPaidRate] = useState('')
  const [paidRateOn, setPaidRateOn] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Two load failures of their own, apart from `error`, which carries form validation. Sharing it
  // made a mistyped rate read as a failed list, and a failed read leave the list on its loading hint.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load())
      setLoadError(null)
    } catch (failure) {
      setRows(null)
      setLoadError(failure instanceof Error ? failure.message : t('supplierInvoice.loadFailed'))
    }
  }, [load, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const openDetail = useCallback(
    async (purchaseOrderId: string) => {
      setDetail(null)
      setDetailError(null)
      try {
        setDetail(await loadOrder(purchaseOrderId))
      } catch (failure) {
        setDetailError(
          failure instanceof Error ? failure.message : t('supplierInvoice.orderLoadFailed'),
        )
      }
    },
    [loadOrder, t],
  )

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    setAnswer(matchOf(write.response) ?? null)
    setInvoiceNumber('')
    setInvoicedOn('')
    setDueOn('')
    // The billed figures go with the invoice that was entered. Left on the form, the next invoice
    // against the same order opens with the last one's numbers already typed, and the idempotency
    // key is per request, so sending them again is a second real invoice.
    setBilled({})
    setBaseTotal('')
    setRate('')
    setRateOn('')
    // The payment form closes for the same reason the invoice form is cleared: its
    // `expectedVersion` is now stale, and a second send of the same figures is a second real
    // payment rather than a retry.
    setPayingId(null)
    setPaidAmount('')
    setPaidBase('')
    setPaidRate('')
    setPaidRateOn('')
    /**
     * Re-read rather than patch. Every write here advances `purchase_order.version` and the next
     * one sends `expectedVersion`, so a screen that kept the version it had would take a 409 that
     * reads to the accountant as somebody else editing the order. The re-read also carries the new
     * `matchStatus`, which is what decides whether the match button is offered at all.
     */
    if (openId !== null) void openDetail(openId)
    void refresh()
  })

  /**
   * The three tolerances, or null when any one of them is missing or out of range.
   *
   * One reader for both routes, because `EnterInvoice` and `RematchInvoice` take the same
   * `MatchTolerancesRequest` and a screen with two copies of this check would drift.
   */
  const tolerances = useCallback((): Record<string, number> | null => {
    const read = (text: string): number | null => {
      const trimmed = text.trim()
      if (trimmed === '') return null
      const value = Number(trimmed)
      // `asBasisPoints`: whole, zero or more, at most 100000. Ten thousand basis points is a
      // hundred per cent, so the ceiling catches a figure typed as a percentage of a percentage.
      return Number.isSafeInteger(value) && value >= 0 && value <= 100_000 ? value : null
    }
    const over = read(overReceipt)
    const quantity = read(invoiceQuantity)
    const price = read(priceTolerance)
    if (over === null || quantity === null || price === null) return null
    return {
      overReceiptBasisPoints: over,
      invoiceQuantityBasisPoints: quantity,
      priceBasisPoints: price,
    }
  }, [invoiceQuantity, overReceipt, priceTolerance])

  const enterInvoice = useCallback(
    async (order: PurchaseOrderDetailData, currency: string) => {
      if (invoiceNumber.trim() === '') {
        setError(t('supplierInvoice.numberRequired'))
        return
      }
      if (invoicedOn === '') {
        setError(t('supplierInvoice.invoicedOnRequired'))
        return
      }
      const limits = tolerances()
      if (limits === null) {
        setError(t('supplierInvoice.tolerancesRequired'))
        return
      }

      const lines: InvoiceLineBody[] = []
      const pricePerOrderLine = new Map<string, string>()
      // Walked in the order the receipts are read in rather than in the order the boxes were
      // typed, so the `position` the service writes on each invoice line is the delivery's order.
      for (const receiptLine of order.receipts.flatMap((receipt) => receipt.lines)) {
        const draft = billed[receiptLine.goodsReceiptLineId] ?? EMPTY_BILLED
        if (draft.quantity.trim() === '' && draft.unitCost.trim() === '') continue
        const quantity = Number(draft.quantity.trim())
        if (!Number.isSafeInteger(quantity) || quantity < 1) {
          setError(t('supplierInvoice.quantityWhole'))
          return
        }
        let unitCostMinor: string
        try {
          unitCostMinor = parseMajorInput(draft.unitCost, currency).toString()
        } catch (inputError) {
          setError(
            inputError instanceof MoneyInputError
              ? inputError.message
              : t('supplierInvoice.costInvalid'),
          )
          return
        }
        const already = pricePerOrderLine.get(receiptLine.purchaseOrderLineId)
        if (already !== undefined && already !== unitCostMinor) {
          /**
           * `INVOICE_LINE_PRICES_DIFFER`, a 422 this screen can see coming.
           *
           * The domain keys an invoice line by order line and the schema cites one receipt line per
           * invoice line, so two deliveries against one order line arrive at the match as two
           * invoice lines and are summed per order line. Summing needs one price, and the service
           * refuses the second rather than choosing between them.
           */
          setError(t('supplierInvoice.onePricePerLine'))
          return
        }
        pricePerOrderLine.set(receiptLine.purchaseOrderLineId, unitCostMinor)
        lines.push({
          goodsReceiptLineId: receiptLine.goodsReceiptLineId,
          quantityInvoiced: quantity,
          unitCostMinor,
        })
      }
      if (lines.length === 0) {
        setError(t('supplierInvoice.nothingBilled'))
        return
      }

      let foreignFields: Record<string, string> = {}
      if (currency !== baseCurrency) {
        // `IMP-004`: all three or none, and the service refuses a partial set with
        // `FOREIGN_RATE_REQUIRED`. The base amount is supplied rather than derived, because
        // converting needs a rounding and `MR-04` gives this no point to round at.
        if (baseTotal.trim() === '' || rate.trim() === '' || rateOn === '') {
          setError(t('supplierInvoice.rateRequired'))
          return
        }
        if (!RATE.test(rate.trim())) {
          setError(t('supplierInvoice.rateInvalid'))
          return
        }
        let baseMinor: bigint
        try {
          baseMinor = parseMajorInput(baseTotal, baseCurrency)
        } catch (inputError) {
          setError(
            inputError instanceof MoneyInputError
              ? inputError.message
              : t('supplierInvoice.baseTotalInvalid'),
          )
          return
        }
        if (baseMinor <= 0n) {
          // `asPositiveMinor`: greater than zero. An import booked at nothing is not a conversion.
          setError(t('supplierInvoice.baseTotalPositive'))
          return
        }
        foreignFields = {
          baseTotalMinor: baseMinor.toString(),
          exchangeRate: rate.trim(),
          exchangeRateOn: rateOn,
        }
      }

      setError(null)
      setAnswer(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/purchase-orders/${order.id}/invoices`,
        label: t('supplierInvoice.queueEnterLabel', { number: order.number }),
        body: {
          // `ARC-007`: the match moves the order, so two people entering invoices against it is a
          // conflict rather than two matches.
          expectedVersion: order.version,
          supplierInvoiceNumber: invoiceNumber.trim(),
          invoicedOn,
          ...(dueOn === '' ? {} : { dueOn }),
          lines,
          // Omitted entirely in the base currency: sending them is `NOT_A_FOREIGN_AMOUNT`.
          ...foreignFields,
          tolerances: limits,
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [
      baseCurrency,
      baseTotal,
      billed,
      dueOn,
      enqueue,
      invoiceNumber,
      invoicedOn,
      rate,
      rateOn,
      t,
      tolerances,
    ],
  )

  const rematch = useCallback(
    async (order: PurchaseOrderDetailData, invoice: SupplierInvoiceDetail) => {
      const limits = tolerances()
      if (limits === null) {
        setError(t('supplierInvoice.tolerancesRequired'))
        return
      }
      setError(null)
      setAnswer(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/purchase-orders/${order.id}/invoices/${invoice.id}/match`,
        label: t('supplierInvoice.queueMatchLabel', { number: invoice.supplierInvoiceNumber }),
        // The stored lines are re-read by the service, so only the tolerances are sent: judging the
        // same invoice again has to compare the same figures the supplier billed.
        body: { expectedVersion: order.version, tolerances: limits },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t, tolerances],
  )

  const resolve = useCallback(
    async (order: PurchaseOrderDetailData) => {
      setError(null)
      setAnswer(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/purchase-orders/${order.id}/dispute-resolution`,
        label: t('supplierInvoice.queueResolveLabel', { number: order.number }),
        body: { expectedVersion: order.version },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, t],
  )

  /**
   * `IMP-004`, `decisions/0041`: record that the supplier was paid.
   *
   * **Three refusals are taken locally, because a refused write holds the whole queue** and this one
   * sits behind taking payment at the counter.
   *
   * - `SETTLEMENT_EXCEEDS_INVOICE` (422): more than is left to pay. The figure is on screen, so the
   *   screen can compare.
   * - `FOREIGN_SETTLEMENT_MUST_CLEAR` (422): an invoice in another currency is paid in one payment,
   *   because part-paying it would split the base-currency value it was booked at and `MR-04` has no
   *   rounding point for that division. The form has no amount box at all in that case, so the
   *   refusal is unreachable rather than explained.
   * - `FOREIGN_RATE_REQUIRED` and `NOT_A_FOREIGN_AMOUNT`: the three rate fields appear exactly when
   *   the invoice currency differs from the branch's, the same way the invoice form drives them.
   */
  const settle = useCallback(
    async (
      order: PurchaseOrderDetailData,
      invoice: SupplierInvoiceDetail,
      currency: string,
      outstandingMinor: bigint,
      version: number,
    ) => {
      if (paidOn === '') {
        setError(t('supplierInvoice.settledOnRequired'))
        return
      }
      const isForeign = currency !== baseCurrency
      let settledMinor = outstandingMinor
      if (!isForeign) {
        try {
          settledMinor = parseMajorInput(paidAmount, currency)
        } catch (inputError) {
          setError(
            inputError instanceof MoneyInputError
              ? inputError.message
              : t('supplierInvoice.settleAmountInvalid'),
          )
          return
        }
        if (settledMinor <= 0n) {
          setError(t('supplierInvoice.settleAmountPositive'))
          return
        }
        if (settledMinor > outstandingMinor) {
          setError(
            t('supplierInvoice.settleTooMuch', {
              amount: formatWireForDisplay(outstandingMinor.toString(), currency),
            }),
          )
          return
        }
      }

      let foreignFields: Record<string, string> = {}
      if (isForeign) {
        if (paidBase.trim() === '' || paidRate.trim() === '' || paidRateOn === '') {
          setError(t('supplierInvoice.settleRateRequired'))
          return
        }
        if (!RATE.test(paidRate.trim())) {
          setError(t('supplierInvoice.settleRateInvalid'))
          return
        }
        let baseMinor: bigint
        try {
          baseMinor = parseMajorInput(paidBase, baseCurrency)
        } catch (inputError) {
          setError(
            inputError instanceof MoneyInputError
              ? inputError.message
              : t('supplierInvoice.settleBaseInvalid'),
          )
          return
        }
        if (baseMinor <= 0n) {
          setError(t('supplierInvoice.settleBasePositive'))
          return
        }
        foreignFields = {
          settledBaseMinor: baseMinor.toString(),
          exchangeRate: paidRate.trim(),
          exchangeRateOn: paidRateOn,
        }
      }

      setError(null)
      setAnswer(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/purchase-orders/${order.id}/invoices/${invoice.id}/settlements`,
        label: t('supplierInvoice.queueSettleLabel', { number: invoice.supplierInvoiceNumber }),
        body: {
          // The invoice's version, not the order's: this write locks and bumps the invoice.
          expectedVersion: version,
          settledOn: paidOn,
          paidFrom,
          settledMinor: settledMinor.toString(),
          ...foreignFields,
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [baseCurrency, enqueue, paidAmount, paidBase, paidFrom, paidOn, paidRate, paidRateOn, t],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.supplierInvoice'))

  const findingWords = (finding: Finding): string => {
    const what = matchFindingWord(t, finding.kind)
    if (finding.quantity !== undefined) {
      return t('supplierInvoice.findingPieces', { what, count: finding.quantity })
    }
    if (finding.amount !== undefined) {
      return t('supplierInvoice.findingAmount', {
        what,
        amount: formatWireForDisplay(finding.amount.minor, finding.amount.currency),
      })
    }
    return what
  }

  /**
   * The order's currency, narrowed once into a `const`.
   *
   * Every amount below is parsed and formatted at this currency's decimal places, and narrowing a
   * property access does not survive into a callback: pulled out here, the map callbacks over the
   * receipt lines cannot reach for a currency that might be absent.
   */
  const currency = detail?.currency

  /**
   * The payment half of one invoice row. `IMP-004`, `decisions/0041`.
   *
   * **Four reasons to render nothing, and not one of them is a disabled button.** No
   * `supplier_invoice_settle` row, in which case paying is not this person's job at all; no
   * `outstandingMinor`, which is Appendix B's cost row withheld and leaves the screen unable to
   * refuse an over-payment locally; no invoice `version`, so there is nothing to send as
   * `expectedVersion`; and nothing left to pay, which is the one case that gets a sentence because
   * it is an answer rather than an absence.
   *
   * **The amount box exists only in the branch's own currency.** `FOREIGN_SETTLEMENT_MUST_CLEAR`
   * refuses a part payment of an imported invoice, so rather than explain that refusal the form
   * names the whole figure and sends it. A box that could produce a 422 that halts the queue is
   * worse than no box.
   */
  const settleForm = (invoice: SupplierInvoiceDetail): JSX.Element | null => {
    if (!canSettle || detail === null || currency === undefined) return null
    const left = invoice.outstandingMinor
    const version = invoice.version
    // Validated rather than trusted: `reads.ts` maps this off the wire, and a `BigInt('')` here
    // would throw inside a render and take the screen down with it.
    if (left === undefined || version === undefined || !/^-?\d+$/.test(left)) return null
    const outstandingMinor = BigInt(left)
    if (outstandingMinor <= 0n) {
      return <span className="lm-invoice__why">{t('supplierInvoice.settleFullyPaid')}</span>
    }
    if (payingId !== invoice.id) {
      return (
        <button
          type="button"
          className="lm-button lm-button--secondary"
          data-settle-open={invoice.id}
          onClick={() => {
            setError(null)
            setPayingId(invoice.id)
          }}
        >
          {t('supplierInvoice.settleOpen')}
        </button>
      )
    }

    const isForeign = currency !== baseCurrency
    return (
      <div className="lm-settle" data-settle-invoice={invoice.id}>
        <h3 className="lm-settle__heading">{t('supplierInvoice.settleHeading')}</h3>

        {isForeign ? (
          <p className="lm-hint">
            {t('supplierInvoice.settleWholeInvoice', {
              amount: formatWireForDisplay(left, currency),
            })}
          </p>
        ) : (
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">
              {t('supplierInvoice.settleAmountLabel', { currency })}
            </span>
            <input
              className="lm-input"
              name="settleAmount"
              inputMode="decimal"
              autoComplete="off"
              value={paidAmount}
              onChange={(e) => {
                setPaidAmount(e.currentTarget.value)
              }}
            />
          </label>
        )}

        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('supplierInvoice.settlePaidFromLabel')}</span>
          <select
            className="lm-input"
            name="settlePaidFrom"
            value={paidFrom}
            onChange={(e) => {
              setPaidFrom(e.currentTarget.value === 'cash' ? 'cash' : 'bank')
            }}
          >
            <option value="bank">{t('supplierInvoice.settlePaidFromBank')}</option>
            <option value="cash">{t('supplierInvoice.settlePaidFromCash')}</option>
          </select>
        </label>

        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('supplierInvoice.settledOnLabel')}</span>
          <input
            className="lm-input"
            name="settledOn"
            type="date"
            value={paidOn}
            onChange={(e) => {
              setPaidOn(e.currentTarget.value)
            }}
          />
        </label>

        {isForeign ? (
          <>
            {/* `IMP-004`: all three or none. What left the bank is stated rather than converted
                from the rate, because converting needs a rounding `MR-04` does not have a point
                for, which is the same reason the invoice itself carries a booked base amount. */}
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('supplierInvoice.settleBaseLabel', { currency: baseCurrency })}
              </span>
              <input
                className="lm-input"
                name="settleBase"
                inputMode="decimal"
                autoComplete="off"
                value={paidBase}
                onChange={(e) => {
                  setPaidBase(e.currentTarget.value)
                }}
              />
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('supplierInvoice.settleRateLabel')}</span>
              <input
                className="lm-input"
                name="settleRate"
                inputMode="decimal"
                autoComplete="off"
                value={paidRate}
                onChange={(e) => {
                  setPaidRate(e.currentTarget.value)
                }}
              />
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">{t('supplierInvoice.settleRateOnLabel')}</span>
              <input
                className="lm-input"
                name="settleRateOn"
                type="date"
                value={paidRateOn}
                onChange={(e) => {
                  setPaidRateOn(e.currentTarget.value)
                }}
              />
            </label>

            <p className="lm-hint">{t('supplierInvoice.settleDifferenceGoesSeparately')}</p>
          </>
        ) : null}

        <button
          type="button"
          className="lm-button"
          data-settle-submit={invoice.id}
          onClick={() => void settle(detail, invoice, currency, outstandingMinor, version)}
        >
          {t('supplierInvoice.settle')}
        </button>
      </div>
    )
  }

  return (
    <section className="lm-screen" aria-labelledby="supplier-invoice-heading">
      <h1 id="supplier-invoice-heading" className="lm-screen__heading">
        {t('supplierInvoice.heading')}
      </h1>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {answer === null ? null : (
        <div className="lm-match-answer">
          <p className="lm-hint">
            {t('supplierInvoice.verdict', { verdict: matchVerdictWord(t, answer.verdict) })}
          </p>
          {answer.findings.length === 0 ? (
            <p className="lm-hint">{t('supplierInvoice.noFindings')}</p>
          ) : (
            <ul className="lm-finding-list">
              {answer.findings.map((finding) => (
                <li key={`${finding.kind}|${finding.purchaseOrderLineId}`} className="lm-finding">
                  <span className="lm-finding__what">{findingWords(finding)}</span>
                  {/* A blocking finding opens a query and the order does not match; one inside
                      tolerance is recorded and paid. The two have opposite consequences for the
                      money, so they are not one word. */}
                  <span className="lm-finding__weight">
                    {finding.blocking
                      ? t('supplierInvoice.findingBlocks')
                      : finding.withinTolerance
                        ? t('supplierInvoice.findingWithin')
                        : t('supplierInvoice.findingNoted')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/*
        §8 `ST-02`. This list has no filter: it is the server's open view, so empty means nothing is
        open, which the sentence says, and there is no filtered variant to invent.
      */}
      <DataSurface
        state={
          loadError !== null
            ? { kind: 'error', message: loadError, onRetry: () => void refresh() }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('purchasing.what')}
        emptyMessage={t('supplierInvoice.empty')}
      >
        <ul className="lm-po-list">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="lm-po">
              <span className="lm-po__number" dir="ltr">
                <Ltr>{row.number}</Ltr>
              </span>
              <span className="lm-po__status">{purchaseOrderStatusWord(t, row.status)}</span>
              <span className="lm-po__invoices">
                {row.invoices.length === 0
                  ? t('supplierInvoice.noneEntered')
                  : t('supplierInvoice.entered', { count: row.invoices.length })}
              </span>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                aria-expanded={openId === row.id}
                onClick={() => {
                  if (openId === row.id) {
                    setOpenId(null)
                    setDetail(null)
                    return
                  }
                  setOpenId(row.id)
                  setAnswer(null)
                  void openDetail(row.id)
                }}
              >
                {t('supplierInvoice.open')}
              </button>
            </li>
          ))}
        </ul>
      </DataSurface>

      {openId === null ? null : detail === null ? (
        <DataSurface
          state={
            detailError !== null
              ? { kind: 'error', message: detailError, onRetry: () => void openDetail(openId) }
              : { kind: 'loading' }
          }
          shape="rows"
          rows={3}
          what={t('supplierInvoice.orderWhat')}
        >
          {null}
        </DataSurface>
      ) : currency === undefined ? (
        // `ROLE-001a`: this order's costs, and with them its currency, are withheld from this
        // reader, and a supplier invoice is a cost-price document. Nothing is offered rather than a
        // form that would send an amount at a scale this screen guessed.
        //
        // This is the `denied` state of `ST-02` and it now says so. It was a bare `lm-hint`
        // paragraph outside the component because `surface.denied` stops at "you are not allowed to
        // see {what}" and the sentence that helps here names the remedy. `deniedMessage` carries
        // that sentence, so the refusal keeps `role="status"` and the denied styling instead of
        // reading as an aside.
        <DataSurface
          state={{ kind: 'denied' }}
          shape="rows"
          what={t('supplierInvoice.orderWhat')}
          deniedMessage={t('supplierInvoice.costWithheld')}
        >
          {null}
        </DataSurface>
      ) : (
        <div className="lm-invoice-desk">
          <h2 className="lm-invoice-desk__heading">
            {sentence(t, 'supplierInvoice.forOrder', { number: <Ltr>{detail.number}</Ltr> })}
          </h2>

          {detail.totalMinor === undefined ? null : (
            <p className="lm-invoice-desk__total" dir="ltr">
              {t('supplierInvoice.orderTotal', {
                amount: formatWireForDisplay(detail.totalMinor, currency),
              })}
            </p>
          )}

          {detail.invoices.length === 0 ? null : (
            <ul className="lm-invoice-list">
              {detail.invoices.map((invoice) => (
                <li key={invoice.id} className="lm-invoice">
                  <span className="lm-invoice__number" dir="ltr">
                    <Ltr>{invoice.supplierInvoiceNumber}</Ltr>
                  </span>
                  <span className="lm-invoice__state">
                    {invoiceMatchStatusWord(t, invoice.matchStatus)}
                  </span>
                  {invoice.queryReason === undefined ? null : (
                    // The blocking findings of the match that queried it, as the service wrote
                    // them: a kind and an order line id each. Shown as given rather than reworded,
                    // because this is what the accountant quotes back to the wholesaler.
                    <span className="lm-invoice__why">
                      {t('supplierInvoice.queryReason', { reason: invoice.queryReason })}
                    </span>
                  )}

                  {invoice.settledMinor === undefined ||
                  invoice.outstandingMinor === undefined ? null : (
                    <span className="lm-invoice__money" data-invoice-money={invoice.id} dir="ltr">
                      {t('supplierInvoice.paidAndLeft', {
                        paid: formatWireForDisplay(invoice.settledMinor, currency),
                        left: formatWireForDisplay(invoice.outstandingMinor, currency),
                      })}
                    </span>
                  )}

                  {invoice.matchStatus !== 'unmatched' ? null : MATCHABLE.includes(
                      detail.status,
                    ) ? (
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => void rematch(detail, invoice)}
                    >
                      {t('supplierInvoice.matchAgain')}
                    </button>
                  ) : (
                    <span className="lm-invoice__why">{t('supplierInvoice.cannotMatchYet')}</span>
                  )}

                  {settleForm(invoice)}
                </li>
              ))}
            </ul>
          )}

          {detail.status === 'disputed' ? (
            canResolveDispute ? (
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => void resolve(detail)}
              >
                {t('supplierInvoice.resolve')}
              </button>
            ) : (
              // W.5 requires a manager and the domain throws `MANAGER_REQUIRED`, so the accountant
              // who raised the query is told who closes it instead of taking a refusal that would
              // then hold every write behind it.
              <p className="lm-hint">{t('supplierInvoice.resolveNeedsManager')}</p>
            )
          ) : null}

          {!MATCHABLE.includes(detail.status) ? (
            // `MATCH_NOT_ALLOWED`: an order with nothing delivered against it has nothing for an
            // invoice to be matched to, and one already matched or closed is finished with. The
            // state is named rather than left as a missing form.
            <p className="lm-hint">
              {t('supplierInvoice.notMatchable', {
                status: purchaseOrderStatusWord(t, detail.status),
              })}
            </p>
          ) : (
            <>
              {detail.status === 'partially_received' ? (
                // Not a refusal, a result: while the order is part-delivered every verdict is
                // `pending`, the invoice is stored unmatched and the order does not move.
                <p className="lm-hint">{t('supplierInvoice.willBePending')}</p>
              ) : null}

              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('supplierInvoice.numberLabel')}</span>
                <input
                  className="lm-input"
                  autoComplete="off"
                  value={invoiceNumber}
                  onChange={(e) => {
                    setInvoiceNumber(e.currentTarget.value)
                  }}
                />
              </label>

              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('supplierInvoice.invoicedOnLabel')}</span>
                <input
                  className="lm-input"
                  type="date"
                  value={invoicedOn}
                  onChange={(e) => {
                    setInvoicedOn(e.currentTarget.value)
                  }}
                />
              </label>

              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">{t('supplierInvoice.dueOnLabel')}</span>
                <input
                  className="lm-input"
                  type="date"
                  value={dueOn}
                  onChange={(e) => {
                    setDueOn(e.currentTarget.value)
                  }}
                />
              </label>

              {detail.receipts.length === 0 ? (
                <p className="lm-hint">{t('supplierInvoice.noReceiptLines')}</p>
              ) : (
                <ul className="lm-receipt-line-list">
                  {detail.receipts.flatMap((receipt) =>
                    receipt.lines.map((receiptLine) => {
                      const orderLine = detail.lines.find(
                        (candidate) => candidate.id === receiptLine.purchaseOrderLineId,
                      )
                      const draft = billed[receiptLine.goodsReceiptLineId] ?? EMPTY_BILLED
                      const what = orderLine?.description ?? t('supplierInvoice.lineNotOnOrder')
                      return (
                        <li key={receiptLine.goodsReceiptLineId} className="lm-receipt-line">
                          <span className="lm-receipt-line__what">{what}</span>
                          <span className="lm-receipt-line__meta" dir="ltr">
                            <Ltr>{`${receipt.number} ${orderLine?.sku ?? ''}`}</Ltr>
                          </span>

                          {/* Two of the three statements the match compares, printed beside the
                              third rather than into it. */}
                          {orderLine === undefined ? null : (
                            <span className="lm-receipt-line__figures" dir="ltr">
                              {t('supplierInvoice.orderedAndReceived', {
                                ordered: orderLine.orderedQuantity,
                                received: receiptLine.quantityReceived,
                              })}
                            </span>
                          )}

                          {orderLine?.unitCostMinor === undefined ? null : (
                            <span className="lm-receipt-line__agreed" dir="ltr">
                              {t('supplierInvoice.agreedCost', {
                                amount: formatWireForDisplay(orderLine.unitCostMinor, currency),
                              })}
                            </span>
                          )}

                          {receiptLine.isOverReceipt ? (
                            // Already accepted within whatever tolerance the dock typed, so it is
                            // context for the figure rather than a thing to act on here.
                            <span className="lm-receipt-line__over">
                              {t('supplierInvoice.wasOverReceipt')}
                            </span>
                          ) : null}

                          <label className="lm-field lm-field--inline">
                            <span className="lm-field__label">
                              {t('supplierInvoice.billedQuantityLabel', { what })}
                            </span>
                            <input
                              className="lm-input"
                              inputMode="numeric"
                              autoComplete="off"
                              value={draft.quantity}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setBilled((previous) => ({
                                  ...previous,
                                  [receiptLine.goodsReceiptLineId]: {
                                    ...(previous[receiptLine.goodsReceiptLineId] ?? EMPTY_BILLED),
                                    quantity: value,
                                  },
                                }))
                              }}
                            />
                          </label>

                          <label className="lm-field lm-field--inline">
                            <span className="lm-field__label">
                              {t('supplierInvoice.billedCostLabel', { what, currency })}
                            </span>
                            <input
                              className="lm-input"
                              inputMode="decimal"
                              autoComplete="off"
                              value={draft.unitCost}
                              onChange={(e) => {
                                const value = e.currentTarget.value
                                setBilled((previous) => ({
                                  ...previous,
                                  [receiptLine.goodsReceiptLineId]: {
                                    ...(previous[receiptLine.goodsReceiptLineId] ?? EMPTY_BILLED),
                                    unitCost: value,
                                  },
                                }))
                              }}
                            />
                          </label>
                        </li>
                      )
                    }),
                  )}
                </ul>
              )}
              {/* Nothing above is filled in from the order or the receipt, which is the whole
                  point of a three-way match. */}
              <p className="lm-hint">{t('supplierInvoice.nothingPrefilled')}</p>

              {currency === baseCurrency ? null : (
                <>
                  {/* `IMP-004`. Required because the currencies differ, and refused outright when
                      they do not, so the fields are not merely optional. */}
                  <p className="lm-hint">
                    {t('supplierInvoice.foreignMeans', {
                      invoiceCurrency: currency,
                      baseCurrency,
                    })}
                  </p>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('supplierInvoice.baseTotalLabel', { currency: baseCurrency })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={baseTotal}
                      onChange={(e) => {
                        setBaseTotal(e.currentTarget.value)
                      }}
                    />
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('supplierInvoice.rateLabel')}</span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={rate}
                      onChange={(e) => {
                        setRate(e.currentTarget.value)
                      }}
                    />
                  </label>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('supplierInvoice.rateOnLabel')}</span>
                    <input
                      className="lm-input"
                      type="date"
                      value={rateOn}
                      onChange={(e) => {
                        setRateOn(e.currentTarget.value)
                      }}
                    />
                  </label>
                </>
              )}
            </>
          )}

          {/* The tolerances sit outside the invoice form because the match button needs them too,
              and both routes take the same three. */}
          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('supplierInvoice.overReceiptLabel')}</span>
            <input
              className="lm-input"
              inputMode="numeric"
              autoComplete="off"
              value={overReceipt}
              onChange={(e) => {
                setOverReceipt(e.currentTarget.value)
              }}
            />
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('supplierInvoice.invoiceQuantityLabel')}</span>
            <input
              className="lm-input"
              inputMode="numeric"
              autoComplete="off"
              value={invoiceQuantity}
              onChange={(e) => {
                setInvoiceQuantity(e.currentTarget.value)
              }}
            />
          </label>

          <label className="lm-field lm-field--inline">
            <span className="lm-field__label">{t('supplierInvoice.priceToleranceLabel')}</span>
            <input
              className="lm-input"
              inputMode="numeric"
              autoComplete="off"
              value={priceTolerance}
              onChange={(e) => {
                setPriceTolerance(e.currentTarget.value)
              }}
            />
          </label>
          {/* No defaults, because `INV-006` names none and nothing stores one. The consequence is
              said out loud rather than left as three unexplained required fields. */}
          <p className="lm-hint">{t('supplierInvoice.tolerancesHint')}</p>

          {MATCHABLE.includes(detail.status) ? (
            <button
              type="button"
              className="lm-button lm-button--primary"
              onClick={() => void enterInvoice(detail, currency)}
            >
              {t('supplierInvoice.enter')}
            </button>
          ) : null}
        </div>
      )}
    </section>
  )
}
